import { z } from 'zod';

/*
 * O papel global do usuário no CRM, num módulo sem dependências (#761,
 * 2026-09-27 17:09 +0200). Mora fora de `crm.ts` porque `salesSplit.ts`
 * precisa compará-lo (`saleSplitInternals`) e `crm.ts` já importa de
 * `salesSplit.ts` — importar de volta seria um ciclo, que o
 * `import-x/no-cycle` reprova. `crm.ts` reexporta os dois nomes; quem importa
 * de `@etiquetachef/types` não percebe a mudança.
 */

/**
 * Papel global do usuário no CRM.
 * - ADMIN: acesso total, gestão de colaboradores.
 * - MANAGER/SELLER: operam o comercial (quadros/leads/clientes).
 * - AUDITOR: acesso somente-leitura ao comercial e aos clientes; nunca escreve.
 * - PARTNER (#743, F8 da #679): parceiro de revenda — usuário EXTERNO. Vê só o
 *   próprio quadro, os próprios leads e clientes e o portal. O perímetro é
 *   fechado por papel no servidor (`assertActiveCrmUser` o recusa; só a
 *   allowlist de `partnerPerimeter.test.ts` abre) e nas rules
 *   (`isCrmUserActive()` é só interno).
 */
export const CrmRoleSchema = z.enum(['ADMIN', 'MANAGER', 'SELLER', 'AUDITOR', 'PARTNER']);
export type CrmRole = z.infer<typeof CrmRoleSchema>;
/**
 * Os papéis do CRM como constantes (`CrmRole.PARTNER`), derivadas do schema.
 *
 * Origem: #761 — 2026-09-27 17:09 +0200 (o papel nasceu em `2bad673b3`,
 * 2026-07-07, Fase 1 do CRM; `PARTNER` na #743).
 *
 * Por quê: `'PARTNER'` tem oito sentidos no código (papel, modelo do
 * parceiro, modo do tenant, origem do item, split, dossiê…). O nome da
 * constante diz QUAL, e comparar um valor `unknown` (o `role` lido cru do
 * Firestore) com uma constante pega erro de digitação que o literal deixaria
 * passar calado. A regra `etiquetachef/no-concept-literal` (`eslint.config.mjs`)
 * reprova a comparação com o literal.
 *
 * Contrato: `CrmRoleSchema.enum` — o mesmo objeto que o Zod monta, então
 * schema e constante não podem divergir. Os valores gravados não mudam.
 *
 * Usado por: Functions (`crmCollaboratorGuard`, `getSaleCheckoutOptions`,
 * pós-venda do parceiro), CRM Functions (convites, permissões, quadros) e CRM
 * web (`apps/crm`: rotas, navegação, permissões, Contratação).
 *
 * Dicas: `CrmRole.PARTNER` e `CommercialMode.PARTNER` têm o MESMO tipo literal
 * `'PARTNER'` — o TS não impede trocar um pelo outro. Escolha a constante do
 * conceito que o campo guarda.
 *
 * Issues: #761, #743 (papel PARTNER).
 */
export const CrmRole = CrmRoleSchema.enum;
